<template>
  <main class="service-options">
    <section class="section options-hero">
      <div class="shell hero-grid">
        <header class="page-head compact">
          <h1>Which service do I need?</h1>
          <p>
            Whether you want ongoing oversight or help with a problem already in front of you,
            Centerfire can help determine the right level of response.
          </p>
        </header>
        <aside class="hero-note">
          <span>Not sure?</span>
          <h2>Give us a call.</h2>
          <p>Tell us what the machine is doing and where it is. We will help classify the need.</p>
          <a class="phone-link" href="tel:+12104454277">{{ brand.phone }}</a>
          <span class="hero-or">or</span>
          <router-link class="btn btn-primary" to="/services-request">Describe the situation</router-link>
        </aside>
      </div>
    </section>

    <section class="section options-section">
      <div class="shell">
        <div class="options-grid">
          <article id="managed-maintenance" class="option-card option-featured">
            <div class="option-topline">
              <p class="option-type">Ongoing</p>
              <span>01</span>
            </div>
            <h2>Managed Maintenance Plans</h2>
            <p class="best-for">Continuous oversight by our team instead of reactive maintenance.</p>
            <p>
              We track service intervals, equipment condition, completed repairs, and developing concerns.
              The result is an organized maintenance history and a clear view of what each machine needs next.
            </p>
            <router-link class="text-link" to="/services/managed-maintenance-systems">Explore managed maintenance →</router-link>
          </article>

          <article id="callout-repair" class="option-card">
            <div class="option-topline">
              <p class="option-type">Urgent</p>
              <span>02</span>
            </div>
            <h2>Right-Away Callout Repair</h2>
            <p class="best-for">Best for equipment that is down now or a problem that has stopped the work.</p>
            <p>
              Callout service prioritizes getting diagnostics and practical repair capability to the machine.
              Response timing depends on location, safety, technician availability, parts, and the nature of the failure.
            </p>
            <router-link class="text-link" to="/services-request">Request callout service →</router-link>
          </article>

          <article id="scheduled-repair" class="option-card">
            <div class="option-topline">
              <p class="option-type">Planned</p>
              <span>03</span>
            </div>
            <h2>Scheduled Repair</h2>
            <p class="best-for">Best for known issues that can be handled deliberately before they become urgent.</p>
            <p>
              Scheduling ahead gives us time to evaluate the concern, coordinate parts, and plan the work around
              your operation. It is often the most efficient path for a machine that can safely remain in service.
            </p>
            <router-link class="text-link" to="/services-request">Plan a repair →</router-link>
          </article>

          <article id="other-support" class="option-card">
            <div class="option-topline">
              <p class="option-type">Situational</p>
              <span>04</span>
            </div>
            <h2>Other Field Support</h2>
            <p class="best-for">Best for unusual needs that do not fit neatly into a standard service category.</p>
            <p>
              This may include off-road recovery assistance, difficult-access situations, or one-off mechanical
              and welding needs. Share the circumstances and we will determine whether we can help directly or
              guide you toward the most practical next step.
            </p>
            <router-link class="text-link" to="/services-request">Tell us what happened →</router-link>
          </article>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import { brand } from '@/data/content';

export default {
  data() {
    return { brand };
  },
};
</script>

<style scoped>
.service-options {
  min-height: 100vh;
  background:
    radial-gradient(circle at 78% 13%, rgba(255, 90, 2, 0.14), transparent 28%),
    var(--black);
}

.options-hero {
  padding-top: clamp(9rem, 14vw, 11rem);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(280px, 0.65fr);
  gap: clamp(2rem, 7vw, 6rem);
  align-items: end;
}

.compact {
  margin: 0;
}

.compact h1 {
  max-width: 13ch;
  font-size: clamp(3rem, 7vw, 6.5rem);
  line-height: 0.94;
  text-transform: uppercase;
}

.compact > p:last-child {
  margin-top: 0.8rem;
  font-size: clamp(1rem, 1.5vw, 1.2rem);
}

.hero-note {
  display: grid;
  gap: 0.8rem;
  padding: 1.3rem;
  border-left: 2px solid var(--orange);
  background: rgba(255, 255, 255, 0.035);
}

.hero-note span,
.option-type {
  color: var(--orange);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.hero-note h2 {
  font-size: clamp(1.5rem, 2.5vw, 2.2rem);
  font-weight: 400;
}

.phone-link {
  width: fit-content;
  color: var(--orange);
  font-family: 'Jost', 'Barlow', sans-serif;
  font-size: clamp(1.35rem, 2.2vw, 1.85rem);
  font-weight: 500;
  letter-spacing: 0.02em;
}

.phone-link:hover,
.phone-link:focus-visible {
  color: var(--white);
}

.hero-note .hero-or {
  color: rgba(255, 255, 255, 0.62);
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  text-transform: lowercase;
}

.hero-note .btn {
  justify-self: start;
  margin-top: 0.3rem;
}

.options-section {
  padding-top: clamp(3rem, 6vw, 5rem);
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.option-card {
  scroll-margin-top: 270px;
  min-height: 390px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: clamp(1.3rem, 3vw, 2.2rem);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: var(--radius);
  background: rgba(0, 0, 0, 0.72);
}

.option-featured {
  border-color: rgba(255, 90, 2, 0.75);
  background: linear-gradient(145deg, rgba(255, 90, 2, 0.1), transparent 52%), rgba(0, 0, 0, 0.78);
}

.option-topline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);
}

.option-topline span {
  color: rgba(255, 255, 255, 0.42);
  font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
}

.option-card h2 {
  max-width: 18ch;
  font-size: clamp(1.7rem, 3vw, 2.7rem);
  font-weight: 400;
}

.option-card > p {
  color: rgba(255, 255, 255, 0.86);
}

.option-card .best-for {
  color: var(--white);
  font-weight: 500;
  line-height: 1.5;
}

.text-link {
  width: fit-content;
  margin-top: auto;
  color: var(--orange);
  font-weight: 500;
}

.text-link:hover,
.text-link:focus-visible {
  color: var(--white);
}

@media (max-width: 820px) {
  .hero-grid,
  .options-grid {
    grid-template-columns: 1fr;
  }

  .option-card {
    min-height: 0;
  }
}
</style>
